import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useToast } from './Toast';

const API_URL = process.env.REACT_APP_API_URL || '/api';

type Section = 'unused' | 'recent' | 'coverage' | 'recommendations';

interface UnusedResource {
  id: string;
  name: string;
  resourceGroup: string;
  subscriptionId: string;
  subscriptionName: string;
  resourceType: string;
  category: string;
  reason: string;
  estimatedMonthlyCostUsd: number;
  lastActivityDate: string | null;
  tags: Record<string, string>;
}

interface RecentResource {
  id: string;
  name: string;
  resourceGroup: string;
  subscriptionId: string;
  subscriptionName: string;
  resourceType: string;
  createdDate: string;
  estimatedMonthlyCostUsd: number;
  tags: Record<string, string>;
}

interface TagUsageStat {
  tagKey: string;
  resourceCount: number;
  coveragePercent: number;
}

interface TagCoverage {
  totalResources: number;
  taggedResources: number;
  untaggedResources: number;
  coveragePercent: number;
  tagUsage: TagUsageStat[];
  untaggedSummary: { resourceType: string; count: number }[];
}

interface Recommendation {
  id: string;
  title: string;
  description: string;
  impact: 'High' | 'Medium' | 'Low';
  potentialMonthlySavingsUsd: number;
  affectedResourceCount: number;
  category: string;
}

interface CostAnalysisReport {
  generatedAt: string;
  subscriptionId: string;
  unusedResources: UnusedResource[];
  recentResources: RecentResource[];
  tagCoverage: TagCoverage;
  recommendations: Recommendation[];
  totalEstimatedWasteUsd: number;
  totalEstimatedMonthlyCostUsd: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  UnattachedDisk: 'Unattached Disk',
  OrphanedNic: 'Orphaned NIC',
  OrphanedPublicIp: 'Orphaned Public IP',
  OldSnapshot: 'Old Snapshot',
  StoppedVm: 'Stopped VM',
  EmptyResourceGroup: 'Empty Resource Group',
  UnusedAppServicePlan: 'Unused App Service Plan',
  UnattachedNsg: 'Unattached NSG',
  EmptyStorageAccount: 'Empty Storage Account',
  IdleSqlDatabase: 'Idle SQL Database',
  UnusedLoadBalancer: 'Unused Load Balancer',
  EmptyKeyVault: 'Empty Key Vault',
};

const IMPACT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  High:   { bg: '#3a1a1a', text: '#ff8f9a', border: '#dc3545' },
  Medium: { bg: '#3a2e00', text: '#ffd966', border: '#ffc107' },
  Low:    { bg: '#0a2a3a', text: '#66c8ff', border: '#007bff' },
};

function formatCost(amount: number): string {
  if (amount === 0) return '-';
  return `$${amount.toFixed(2)}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysAgo(dateStr: string | null): string {
  if (!dateStr) return '-';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

function resourceTypeShort(fullType: string): string {
  return fullType.split('/').pop() ?? fullType;
}

const CostAnalyzer: React.FC = () => {
  const { showToast } = useToast();
  const [report, setReport] = useState<CostAnalysisReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState<Section>('unused');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [recentDays, setRecentDays] = useState(30);

  // Guards against out-of-order responses when the user flips the window quickly:
  // only the latest request may write state.
  const requestSeq = useRef(0);
  const fetchReport = async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await axios.get<CostAnalysisReport>(`${API_URL}/costanalyzer/report`, {
        params: { subscriptionId: 'all', recentDays },
      });
      if (seq !== requestSeq.current) return;
      setReport(res.data);
    } catch (err) {
      if (seq === requestSeq.current) showToast('Failed to load cost analysis report', 'error');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [recentDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = useMemo(() => {
    if (!report) return [];
    const cats = Array.from(new Set(report.unusedResources.map(r => r.category)));
    return cats;
  }, [report]);

  const filteredUnused = useMemo(() => {
    if (!report) return [];
    if (categoryFilter === 'all') return report.unusedResources;
    return report.unusedResources.filter(r => r.category === categoryFilter);
  }, [report, categoryFilter]);

  const coverageColor = (pct: number) =>
    pct >= 80 ? '#28a745' : pct >= 60 ? '#ffc107' : '#dc3545';

  const sectionTabs: { id: Section; label: string }[] = [
    { id: 'unused', label: 'Unused Resources' },
    { id: 'recent', label: `Recent Resources` },
    { id: 'coverage', label: 'Tag Coverage' },
    { id: 'recommendations', label: 'Recommendations' },
  ];

  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--border-radius)',
    padding: '1.25rem 1.5rem',
    flex: 1,
    minWidth: 160,
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
  };

  const thStyle: React.CSSProperties = {
    background: 'var(--bg-table-header)',
    padding: '0.6rem 0.75rem',
    textAlign: 'left',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    verticalAlign: 'top',
  };

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: 1600 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-primary)' }}>Cost Analyzer</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
          <label style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Recent resources window:</label>
          <select
            value={recentDays}
            onChange={e => setRecentDays(Number(e.target.value))}
            style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', padding: '0.4rem 0.75rem' }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchReport}
            disabled={loading}
            style={{ background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.4rem 1rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading && !report && (
        <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '3rem' }}>
          Analyzing resources...
        </div>
      )}

      {report && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <div style={cardStyle}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Estimated Monthly Waste</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#ff8f9a' }}>${report.totalEstimatedWasteUsd.toFixed(0)}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                of ${report.totalEstimatedMonthlyCostUsd.toFixed(0)}/mo total ({report.totalEstimatedMonthlyCostUsd > 0 ? Math.round(report.totalEstimatedWasteUsd / report.totalEstimatedMonthlyCostUsd * 100) : 0}% waste)
              </div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Unused Resources</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)' }}>{report.unusedResources.length}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.25rem' }}>across {new Set(report.unusedResources.map(r => r.category)).size} categories</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Recent Resources</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)' }}>{report.recentResources.length}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.25rem' }}>created in last {recentDays} days</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Tag Coverage</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: coverageColor(report.tagCoverage.coveragePercent) }}>
                {report.tagCoverage.coveragePercent.toFixed(1)}%
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                {report.tagCoverage.taggedResources} / {report.tagCoverage.totalResources} resources tagged
              </div>
            </div>
          </div>

          {/* Section Tabs */}
          <div style={{ borderBottom: '1px solid var(--border-color)', marginBottom: '1.5rem', display: 'flex', gap: '0.25rem' }}>
            {sectionTabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${section === tab.id ? 'active' : ''}`}
                onClick={() => setSection(tab.id)}
              >
                {tab.label}
                {tab.id === 'unused' && report.unusedResources.length > 0 && (
                  <span style={{ marginLeft: '0.4rem', background: 'var(--danger-color)', color: '#fff', borderRadius: '10px', padding: '0 0.4rem', fontSize: '0.7rem' }}>
                    {report.unusedResources.length}
                  </span>
                )}
                {tab.id === 'recommendations' && report.recommendations.filter(r => r.impact === 'High').length > 0 && (
                  <span style={{ marginLeft: '0.4rem', background: 'var(--danger-color)', color: '#fff', borderRadius: '10px', padding: '0 0.4rem', fontSize: '0.7rem' }}>
                    {report.recommendations.filter(r => r.impact === 'High').length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Unused Resources */}
          {section === 'unused' && (
            <div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Filter:</span>
                <button
                  onClick={() => setCategoryFilter('all')}
                  style={{
                    padding: '0.25rem 0.75rem', borderRadius: 12, border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.8rem',
                    background: categoryFilter === 'all' ? 'var(--primary-color)' : 'var(--bg-secondary)',
                    color: categoryFilter === 'all' ? '#fff' : 'var(--text-secondary)',
                  }}
                >
                  All ({report.unusedResources.length})
                </button>
                {categories.map(cat => {
                  const count = report.unusedResources.filter(r => r.category === cat).length;
                  return (
                    <button
                      key={cat}
                      onClick={() => setCategoryFilter(cat)}
                      style={{
                        padding: '0.25rem 0.75rem', borderRadius: 12, border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.8rem',
                        background: categoryFilter === cat ? 'var(--primary-color)' : 'var(--bg-secondary)',
                        color: categoryFilter === cat ? '#fff' : 'var(--text-secondary)',
                      }}
                    >
                      {CATEGORY_LABELS[cat] ?? cat} ({count})
                    </button>
                  );
                })}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Resource Group</th>
                      <th style={thStyle}>Subscription</th>
                      <th style={thStyle}>Category</th>
                      <th style={{ ...thStyle, width: 260 }}>Reason</th>
                      <th style={{ ...thStyle, width: 110, whiteSpace: 'nowrap' }}>Est. Cost/mo</th>
                      <th style={{ ...thStyle, width: 110, whiteSpace: 'nowrap' }}>Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUnused
                      .slice()
                      .sort((a, b) => b.estimatedMonthlyCostUsd - a.estimatedMonthlyCostUsd)
                      .map(r => (
                        <tr key={r.id} style={{ background: 'transparent' }}>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 500 }}>{r.name}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{resourceTypeShort(r.resourceType)}</div>
                          </td>
                          <td style={tdStyle}>{r.resourceGroup}</td>
                          <td style={tdStyle}><span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{r.subscriptionName}</span></td>
                          <td style={tdStyle}>
                            <span style={{ background: 'var(--bg-header)', border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.15rem 0.5rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              {CATEGORY_LABELS[r.category] ?? r.category}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, width: 260, maxWidth: 260, color: 'var(--text-secondary)', fontSize: '0.82rem', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }} title={r.reason}>{r.reason}</td>
                          <td style={{ ...tdStyle, color: r.estimatedMonthlyCostUsd > 0 ? '#ff8f9a' : 'var(--text-secondary)', fontWeight: r.estimatedMonthlyCostUsd > 0 ? 600 : 400 }}>
                            {formatCost(r.estimatedMonthlyCostUsd)}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            {daysAgo(r.lastActivityDate)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Resources */}
          {section === 'recent' && (
            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 0, marginBottom: '1rem' }}>
                Resources created in the last {recentDays} days. Review these to ensure they are tagged correctly and follow naming conventions.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Resource Group</th>
                      <th style={thStyle}>Subscription</th>
                      <th style={thStyle}>Type</th>
                      <th style={thStyle}>Created</th>
                      <th style={thStyle}>Est. Cost/mo</th>
                      <th style={thStyle}>Tags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.recentResources
                      .slice()
                      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
                      .map(r => (
                        <tr key={r.id}>
                          <td style={tdStyle}><span style={{ fontWeight: 500 }}>{r.name}</span></td>
                          <td style={tdStyle}>{r.resourceGroup}</td>
                          <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{r.subscriptionName}</td>
                          <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{resourceTypeShort(r.resourceType)}</td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: '0.875rem' }}>{formatDate(r.createdDate)}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{daysAgo(r.createdDate)}</div>
                          </td>
                          <td style={{ ...tdStyle, color: r.estimatedMonthlyCostUsd > 0 ? '#90ee90' : 'var(--text-secondary)', fontWeight: 500 }}>
                            {formatCost(r.estimatedMonthlyCostUsd)}
                          </td>
                          <td style={tdStyle}>
                            {Object.keys(r.tags).length === 0
                              ? <span style={{ color: '#dc3545', fontSize: '0.8rem' }}>No tags</span>
                              : <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{Object.keys(r.tags).length} tag(s)</span>
                            }
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tag Coverage */}
          {section === 'coverage' && (
            <div style={{ maxWidth: 800 }}>
              {/* Overall bar */}
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>Overall Tag Coverage</span>
                  <span style={{ color: coverageColor(report.tagCoverage.coveragePercent), fontWeight: 700 }}>
                    {report.tagCoverage.coveragePercent.toFixed(1)}%
                  </span>
                </div>
                <div style={{ background: 'var(--bg-header)', borderRadius: 4, height: 12, overflow: 'hidden' }}>
                  <div style={{ width: `${report.tagCoverage.coveragePercent}%`, height: '100%', background: coverageColor(report.tagCoverage.coveragePercent), transition: 'width 0.4s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  <span>{report.tagCoverage.taggedResources} tagged</span>
                  <span>{report.tagCoverage.untaggedResources} untagged</span>
                </div>
              </div>

              {/* Per-tag bars */}
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '1rem', marginTop: 0 }}>Coverage by Tag Key</h4>
              {report.tagCoverage.tagUsage.map(stat => (
                <div key={stat.tagKey} style={{ marginBottom: '0.85rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--text-primary)' }}>{stat.tagKey}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{stat.resourceCount} resources ({stat.coveragePercent.toFixed(1)}%)</span>
                  </div>
                  <div style={{ background: 'var(--bg-header)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${stat.coveragePercent}%`, height: '100%', background: coverageColor(stat.coveragePercent), transition: 'width 0.4s ease' }} />
                  </div>
                </div>
              ))}

              {/* Untagged by type */}
              {report.tagCoverage.untaggedSummary.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '1rem', marginTop: 0 }}>Untagged Resources by Type</h4>
                  <table style={{ ...tableStyle, maxWidth: 400 }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Resource Type</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.tagCoverage.untaggedSummary.map(s => (
                        <tr key={s.resourceType}>
                          <td style={tdStyle}>{s.resourceType}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#ff8f9a', fontWeight: 500 }}>{s.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Recommendations */}
          {section === 'recommendations' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 900 }}>
              {report.recommendations.map(rec => {
                const colors = IMPACT_COLORS[rec.impact] ?? IMPACT_COLORS.Low;
                return (
                  <div key={rec.id} style={{ background: 'var(--bg-secondary)', border: `1px solid var(--border-color)`, borderLeft: `4px solid ${colors.border}`, borderRadius: 'var(--border-radius)', padding: '1.25rem 1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                      <div>
                        <span style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '0.15rem 0.6rem', fontSize: '0.75rem', fontWeight: 600, marginRight: '0.75rem' }}>
                          {rec.impact}
                        </span>
                        <span style={{ background: 'var(--bg-header)', color: 'var(--text-secondary)', borderRadius: 4, padding: '0.15rem 0.6rem', fontSize: '0.75rem' }}>
                          {rec.category}
                        </span>
                      </div>
                      {rec.potentialMonthlySavingsUsd > 0 && (
                        <span style={{ color: '#90ee90', fontWeight: 700, fontSize: '1rem', whiteSpace: 'nowrap' }}>
                          Save ${rec.potentialMonthlySavingsUsd.toFixed(2)}/mo
                        </span>
                      )}
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '1rem', marginBottom: '0.5rem' }}>{rec.title}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.6 }}>{rec.description}</div>
                    {rec.affectedResourceCount > 0 && (
                      <div style={{ marginTop: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                        Affected resources: <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{rec.affectedResourceCount}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div style={{ marginTop: '2rem', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
            Report generated at {new Date(report.generatedAt).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
};

export default CostAnalyzer;
